// fin-core / reading — pure functions over what the ports return: how a balance reads, whether the
// totals agree, and whether the book is current. No I/O, no clock.

import { formatClockParis, formatDay } from './format.js';
import { blockerLabel } from './labels.js';
import type { CustomerBalance, DocumentDetail, JournalLine, LedgerStatus, TrialBalanceLine } from './types.js';

// ── a customer balance, debit-positive ──────────────────────────────────────

export type DueKind = 'owes' | 'advance' | 'settled';

export interface DueReading {
  readonly kind: DueKind;
  /** « À payer » (the customer owes), « Avance » (paid in advance), « Soldé ». */
  readonly label: string;
  /** Always positive: the sign is in `kind`. */
  readonly amountCents: number;
}

export function dueReading(dueCents: number): DueReading {
  if (dueCents > 0) return { kind: 'owes', label: 'À payer', amountCents: dueCents };
  if (dueCents < 0) return { kind: 'advance', label: 'Avance', amountCents: -dueCents };
  return { kind: 'settled', label: 'Soldé', amountCents: 0 };
}

/** What a holder is called on screen: the export's own name, else a plain label. A guess is never made. */
export function holderDisplayName(b: Pick<CustomerBalance, 'holderName' | 'holderKind'> & { holderRef?: string }): string {
  if (b.holderName) return b.holderName;
  // The Lightspeed clearing (C2b-LS): the restaurant's own outlet, not Vega's walk-in.
  if ('holderRef' in b && b.holderRef === 'LS-RESTAURANT') return 'Restaurant (Lightspeed)';
  return b.holderKind === 'anonymous_outlet' ? 'Clients de passage (compte anonyme)' : '(nom non renseigné)';
}

/** What the sum of the listed holders owes, advances excluded and outlets excluded. A debtor total. */
export function debtorTotal(rows: readonly CustomerBalance[]): { owedCents: number; advanceCents: number } {
  let owed = 0;
  let advance = 0;
  for (const r of rows) {
    if (r.holderKind === 'anonymous_outlet') continue;
    if (r.dueCents > 0) owed += r.dueCents;
    else if (r.dueCents < 0) advance += -r.dueCents;
  }
  return { owedCents: owed, advanceCents: advance };
}

// ── a document: what was paid, from the ledger itself ───────────────────────

export interface DocumentPayment {
  readonly entryId: number;
  readonly date: string;
  /** The tender account debited (cards, cash, transfer, vouchers…): that is HOW it was paid. */
  readonly account: string;
  readonly accountLabel: string;
  readonly amountCents: number;
}

const BANK_JOURNAL = 'BKPMS';
const CUSTOMER_ACCOUNT = '411PMS';

/**
 * The payments of a document, read from the ledger's own bank-journal entries: each debit on a tender account
 * (anything but the customer account), with its date. This is exactly what was posted, so it is the reliable
 * payment detail; the Vega movements bearing the same number are shown separately and are not the source.
 */
export function paymentsOf(doc: DocumentDetail): DocumentPayment[] {
  const out: DocumentPayment[] = [];
  for (const e of doc.entries) {
    if (e.journal !== BANK_JOURNAL) continue;
    for (const l of e.lines) {
      if (l.account === CUSTOMER_ACCOUNT || l.debitCents <= 0) continue;
      out.push({ entryId: e.entryId, date: e.entryDate, account: l.account, accountLabel: l.accountLabel, amountCents: l.debitCents });
    }
  }
  return out;
}

// ── the lines of an account: do they add up to its balance? ─────────────────

export interface LinesCheck {
  readonly debitCents: number;
  readonly creditCents: number;
  /** null when not every line is loaded: a partial sum proves nothing and is not compared. */
  readonly agrees: boolean | null;
}

/** The loaded lines against the account's balance line. Only a COMPLETE list is compared. */
export function linesCheck(
  lines: readonly JournalLine[],
  totalLines: number,
  expected: { debitCents: number; creditCents: number },
): LinesCheck {
  let debit = 0;
  let credit = 0;
  for (const l of lines) {
    debit += l.debitCents;
    credit += l.creditCents;
  }
  const complete = lines.length >= totalLines;
  return { debitCents: debit, creditCents: credit, agrees: complete ? debit === expected.debitCents && credit === expected.creditCents : null };
}

// ── the trial balance: the sums that must agree ─────────────────────────────

/** An account's net (debit − credit) as an accountant reads it: an amount and the side it stands on. */
export function balanceSide(netCents: number): { side: 'D' | 'C' | null; amountCents: number } {
  if (netCents > 0) return { side: 'D', amountCents: netCents };
  if (netCents < 0) return { side: 'C', amountCents: -netCents };
  return { side: null, amountCents: 0 };
}

export interface TrialBalanceTotals {
  readonly debitCents: number;
  readonly creditCents: number;
  /** A trial balance whose debits and credits differ is a defect, not a presentation matter. */
  readonly balanced: boolean;
  /** Every line's own net equals its debit − credit. A false here means the read is inconsistent. */
  readonly linesConsistent: boolean;
}

export function trialBalanceTotals(lines: readonly TrialBalanceLine[]): TrialBalanceTotals {
  let debit = 0;
  let credit = 0;
  let consistent = true;
  for (const l of lines) {
    debit += l.debitCents;
    credit += l.creditCents;
    if (l.netCents !== l.debitCents - l.creditCents) consistent = false;
  }
  return { debitCents: debit, creditCents: credit, balanced: debit === credit, linesConsistent: consistent };
}

// ── is the book current? ────────────────────────────────────────────────────

export type HealthLevel = 'ok' | 'attention' | 'alert';

export interface BookHealth {
  readonly level: HealthLevel;
  /** French sentences, most serious first. Never empty. */
  readonly reasons: readonly string[];
}

const RANK: Record<HealthLevel, number> = { ok: 0, attention: 1, alert: 2 };

export function bookHealth(s: LedgerStatus): BookHealth {
  const found: { level: HealthLevel; text: string }[] = [];

  if (s.drift.length > 0) {
    found.push({
      level: 'alert',
      text: `${s.drift.length} écart(s) de dérive : un jour déjà posté a été réécrit par Vega. Il ne se répare pas tout seul.`,
    });
  }
  if (s.openRun) {
    found.push({
      level: 'alert',
      text: `Un passage du poster a démarré à ${formatClockParis(s.openRun.startedAt)} et n'a jamais fini.`,
    });
  }
  if (s.lastRun && s.lastRun.outcome === 'error') {
    found.push({ level: 'alert', text: 'Le dernier passage du poster a échoué.' });
  }
  if (s.posterSilent) {
    found.push({
      level: 'alert',
      text: s.neverRun
        ? "Le poster n'a encore jamais tourné."
        : `Le poster ne répond plus depuis ${s.silentMinutes ?? '?'} minutes.`,
    });
  }
  const next = s.blocked[0];
  if (next) {
    found.push({
      level: 'attention',
      text: `Le ${formatDay(next.day)} est bloqué : ${next.blockers.map(blockerLabel).join(' ; ')}.`,
    });
  }
  if (found.length === 0) {
    found.push({ level: 'ok', text: `Le livre est à jour jusqu'au ${formatDay(s.postedThrough)}.` });
  }

  const level = found.reduce<HealthLevel>((worst, f) => (RANK[f.level] > RANK[worst] ? f.level : worst), 'ok');
  const ordered = [...found].sort((a, b) => RANK[b.level] - RANK[a.level]);
  return { level, reasons: ordered.map((f) => f.text) };
}
